import { Component, computed, DestroyRef, inject, input, output, signal, ViewChild } from '@angular/core';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { OrganizationModel } from '../../../../core/models/data/organization.model';
import { OrganizationContactModel, CreateOrganizationContactRequest } from '../../../../core/models/data/organization-contact.model';
import { OrganizationContactType } from '../../../../core/enums/data.enums';
import { OrganizationService } from '../../../../core/services/organization.service';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { OrganizationContactForm } from '../../../../forms/organization-contact/organization-contact.form';
import { EscCloseDirective } from '../../../../core/directives/esc-close.directive';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { GenericMessagingConstants, OrganizationsMessagingConstants } from '../../../../core/constants/messaging.constants';
import { BaseFormComponent } from '../../../../core/base-classes/base-form-component';
import { CanComponentDeactivate } from '../../../../core/routing/can-deactivate.guard';
import { DirtyFormRegistryService } from '../../../../core/services/dirty-form-registry.service';

@Component({
  selector: 'ms-organization-contact-form-modal',
  standalone: true,
  imports: [FaDuotoneIconComponent, OrganizationContactForm, EscCloseDirective, ConfirmationModalComponent],
  templateUrl: './organization-contact-form-modal.component.html',
  styleUrl: './organization-contact-form-modal.component.scss'
})
export class OrganizationContactFormModalComponent extends BaseFormComponent implements CanComponentDeactivate {

  private organizationService = inject(OrganizationService);
  private bootstrapService = inject(BootstrapService);
  private dirtyFormRegistry = inject(DirtyFormRegistryService);
  private destroyRef = inject(DestroyRef);

  constructor() {
    super();
    this.destroyRef.onDestroy(() => this.dirtyFormRegistry.unregister(this));
  }

  public organization = input<OrganizationModel | undefined>(undefined);
  public contactSaved = output<OrganizationModel>();
  public contactSaveFailed = output<string | undefined>();

  @ViewChild(OrganizationContactForm) contactFormRef!: OrganizationContactForm;
  @ViewChild('contactSaveConfirmationModalRef') saveConfirmationModalRef!: ConfirmationModalComponent;
  @ViewChild('contactLeaveConfirmationModalRef') leaveConfirmationModalRef!: ConfirmationModalComponent;

  public OrganizationContactType = OrganizationContactType;
  public selectedType = signal<OrganizationContactType | undefined>(undefined);
  public isSaving = signal<boolean>(false);

  private modalSelector = '#organizationContactFormModal';
  private saveConfirmationModalSelector = '#organizationContactSaveConfirmationModal';
  private leaveConfirmationModalSelector = '#organizationContactLeaveConfirmationModal';
  private pendingRequest: CreateOrganizationContactRequest | null = null;

  public existingByType = computed<Map<OrganizationContactType, OrganizationContactModel>>(() => {
    const map = new Map<OrganizationContactType, OrganizationContactModel>();
    const contacts = this.organization()?.contacts ?? [];
    for (const contact of contacts) {
      map.set(contact.type, contact);
    }
    return map;
  });

  public selectedContact = computed<OrganizationContactModel | undefined>(() => {
    const type = this.selectedType();
    return type ? this.existingByType().get(type) : undefined;
  });

  public openModal(): void {
    this.selectedType.set(undefined);
    this.dirtyFormRegistry.register(this);
    this.bootstrapService.manageModal(this.modalSelector, ModalActions.Show);
  }

  public selectType(type: OrganizationContactType): void {
    this.selectedType.set(type);
  }

  public backToTypeSelection(): void {
    this.selectedType.set(undefined);
  }

  public onCancel(): void {
    this.selectedType.set(undefined);
    this.bootstrapService.manageModal(this.modalSelector, ModalActions.Hide);
    this.dirtyFormRegistry.unregister(this);
  }

  public canDeactivate(): boolean | Promise<boolean> {
    return this.awaitLeaveConfirmation(this.contactFormRef?.contactForm.dirty ?? false, () => {
      this.leaveConfirmationModalRef.modalType.set(ModalTypes.Question);
      this.leaveConfirmationModalRef.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
      this.bootstrapService.manageModal(this.modalSelector, ModalActions.Hide);
      this.bootstrapService.manageModal(this.leaveConfirmationModalSelector, ModalActions.Show);
    });
  }

  public onLeaveConfirmed(): void {
    this.bootstrapService.manageModal(this.leaveConfirmationModalSelector, ModalActions.Hide);
    this.selectedType.set(undefined);
    this.dirtyFormRegistry.unregister(this);
    this.resolvePendingLeave(true);
  }

  public onLeaveCancelled(): void {
    this.bootstrapService.manageModal(this.leaveConfirmationModalSelector, ModalActions.Hide);
    this.bootstrapService.manageModal(this.modalSelector, ModalActions.Show);
    this.resolvePendingLeave(false);
  }

  public hasContactOfType(type: OrganizationContactType): boolean {
    return this.existingByType().has(type);
  }

  public contactNameForType(type: OrganizationContactType): string | undefined {
    return this.existingByType().get(type)?.name;
  }

  public onContactSubmitted(request: CreateOrganizationContactRequest): void {
    if (this.selectedContact()) {
      this.pendingRequest = request;
      const message = OrganizationsMessagingConstants.ORGANIZATIONS_CONTACT_SAVE_CONFIRMATION()
        .replace('{contactType}', String(this.selectedType()));

      this.saveConfirmationModalRef.modalType.set(ModalTypes.Question);
      this.saveConfirmationModalRef.message.set(message);

      this.bootstrapService.manageModal(this.modalSelector, ModalActions.Hide);
      this.bootstrapService.manageModal(this.saveConfirmationModalSelector, ModalActions.Show);
    } else {
      void this.submitContact(request);
    }
  }

  public async editConfirmationModalConfirmed(): Promise<void> {
    this.bootstrapService.manageModal(this.saveConfirmationModalSelector, ModalActions.Hide);
    const request = this.pendingRequest;
    this.pendingRequest = null;
    if (request) {
      await this.submitContact(request);
    }
  }

  public editConfirmationModalCancelled(): void {
    this.pendingRequest = null;
    this.bootstrapService.manageModal(this.saveConfirmationModalSelector, ModalActions.Hide);
    this.bootstrapService.manageModal(this.modalSelector, ModalActions.Show);
  }

  private async submitContact(request: CreateOrganizationContactRequest): Promise<void> {
    const organizationId = this.organization()?.organizationId;
    if (!organizationId) return;

    this.isSaving.set(true);
    if (this.contactFormRef) {
      this.contactFormRef.isSubmitting = true;
    }

    try {
      const updatedContacts: CreateOrganizationContactRequest[] = [];
      for (const [type, existing] of this.existingByType()) {
        if (type === request.type) continue;
        updatedContacts.push({
          type: existing.type,
          name: existing.name ?? '',
          emailAddress: existing.emailAddress,
          phoneCountryCode: existing.phoneCountryCode,
          phoneNumber: existing.phoneNumber,
          phoneExtension: existing.phoneExtension
        });
      }
      updatedContacts.push(request);

      const result = await this.organizationService.updateOrganizationContacts(organizationId, { contacts: updatedContacts });

      this.bootstrapService.manageModal(this.modalSelector, ModalActions.Hide);
      this.selectedType.set(undefined);
      this.dirtyFormRegistry.unregister(this);

      if (result.success && result.data) {
        this.contactSaved.emit(result.data);
      } else {
        this.contactSaveFailed.emit(result.message);
      }
    } finally {
      this.isSaving.set(false);
      if (this.contactFormRef) {
        this.contactFormRef.isSubmitting = false;
      }
    }
  }
}
